import { Alert, Modal, Table, Tag, Typography } from "antd";
import { get } from "lodash";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "shared/hooks/useApi";
import useScreenJob from "shared/hooks/useScreenJob";
import { NAME_SPACES } from "shared/locales/constants";
import graphql from "utils/api/graphql";
import {
  MOCKUP_IMAGE_ERRORS,
  prepareMockupImage,
} from "utils/helpers/mockup-image";
import { createScreenDraft } from "utils/helpers/screenDraft";
import { generateScreenFromCapture } from "./capture-generation";
import { IMPORT_ACTIONS, planImport, readCaptureFolder } from "./folder-import";

const { Text } = Typography;

const PHASES = {
  REVIEW: "REVIEW",
  SAVING: "SAVING",
  GENERATING: "GENERATING",
  DONE: "DONE",
  ERROR: "ERROR",
};

const RUNS = {
  WAITING: "WAITING",
  RUNNING: "RUNNING",
  DONE: "DONE",
  FAILED: "FAILED",
};

const RUN_TONES = {
  [RUNS.WAITING]: "default",
  [RUNS.RUNNING]: "processing",
  [RUNS.DONE]: "success",
  [RUNS.FAILED]: "error",
};

const ACTION_TONES = {
  [IMPORT_ACTIONS.CREATE]: "blue",
  [IMPORT_ACTIONS.UPDATE]: "default",
  [IMPORT_ACTIONS.CONFLICT]: "warning",
};

const PARALLEL_STEPS = 3;

const runLanes = async (items, worker, lanes) => {
  const queue = [...items];
  await Promise.all(
    Array.from({ length: Math.min(lanes, queue.length) }, async () => {
      while (queue.length) await worker(queue.shift());
    }),
  );
};

const FolderImportModal = ({
  files,
  nodes,
  processId,
  liveStepIds,
  onApplyPlan,
  onClose,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const LABELS = t("CANVAS_EDITOR.FOLDER_IMPORT", { returnObjects: true });
  const CHAT = t("SCREEN_WORKSHOP.CONVERSATION", { returnObjects: true });
  const open = Boolean(files);
  const [phase, setPhase] = useState(PHASES.REVIEW);
  const [runs, setRuns] = useState({});
  const [frozen, setFrozen] = useState(null);
  const { generateHtml: generateScreen, generateState } = useScreenJob({
    active: open,
  });
  const [addScreen] = useMutation(graphql.mutations.ADD_SCREEN);
  const [addScreenVersion] = useMutation(graphql.mutations.ADD_SCREEN_VERSION);
  const [detectSlots] = useMutation(graphql.mutations.DETECT_SLOTS);

  const { steps, ignored } = useMemo(
    () => readCaptureFolder(files || []),
    [files],
  );
  const livePlan = useMemo(() => planImport({ steps, nodes }), [steps, nodes]);
  const plan = frozen || livePlan;
  const importable = plan.filter(
    (step) => step.action !== IMPORT_ACTIONS.CONFLICT,
  );

  const reasonOf = (error) =>
    get(
      {
        [MOCKUP_IMAGE_ERRORS.UNREADABLE]: CHAT.IMAGE_UNREADABLE,
        [MOCKUP_IMAGE_ERRORS.TOO_LARGE]: CHAT.IMAGE_TOO_LARGE,
      },
      get(error, "message"),
    ) ||
    get(error, "reason") ||
    CHAT.GENERATE_ERROR;

  const track = (folder, run) =>
    setRuns((current) => ({
      ...current,
      [folder]: { ...current[folder], ...run },
    }));

  const generateStep = async (step) => {
    track(step.folder, { status: RUNS.RUNNING });
    const states = [];
    const failures = [];
    try {
      const image = await prepareMockupImage(step.screen.file);
      const produced = await generateScreenFromCapture(
        { image, instructions: step.screen.label || undefined },
        {
          generateScreen,
          generateState,
          onScreen: () => undefined,
          onVariant: ({ variant, drawn }) =>
            states.push({
              name: variant.name || drawn.name,
              htmlContent: drawn.htmlContent,
              conditions: variant.conditions || drawn.conditions || [],
            }),
          onVariantError: ({ variant, index, error }) =>
            failures.push({
              name: variant.name || String(index + 1),
              reason: reasonOf(error),
            }),
        },
      );
      if (!produced) return;
      for (const capture of step.states) {
        try {
          const drawn = await generateState({
            image: await prepareMockupImage(capture.file),
            styleContent: produced.css,
            htmlContent: produced.html,
            instructions: capture.label || undefined,
          });
          if (drawn)
            states.push({
              name: capture.label || drawn.name,
              htmlContent: drawn.htmlContent,
              conditions: drawn.conditions || [],
            });
        } catch (error) {
          failures.push({ name: capture.file.name, reason: reasonOf(error) });
        }
      }
      await createScreenDraft({
        addScreen,
        addScreenVersion,
        detectSlots,
        statusId: step.node.id,
        screenId: get(step, "node.data.screenId") || null,
        processId,
        draftOnly: !liveStepIds.includes(step.node.id),
        description: step.name,
        versionNumber: get(step, "node.data.screenVersionNumber") || null,
        htmlContent: produced.html,
        styleContent: produced.css,
        states: states.map((state, index) => ({ ...state, order: index + 1 })),
        copyStates: states.length > 0,
      });
      track(step.folder, {
        status: RUNS.DONE,
        states: states.length,
        failures,
      });
    } catch (error) {
      track(step.folder, {
        status: RUNS.FAILED,
        reason: reasonOf(error),
        failures,
      });
    }
  };

  const onConfirm = async () => {
    setFrozen(plan);
    setPhase(PHASES.SAVING);
    let saved;
    try {
      saved = await onApplyPlan(importable);
    } catch (error) {
      setPhase(PHASES.ERROR);
      return;
    }
    const withScreen = saved.filter((step) => step.screen);
    setRuns(
      Object.fromEntries(
        withScreen.map((step) => [step.folder, { status: RUNS.WAITING }]),
      ),
    );
    setPhase(PHASES.GENERATING);
    await runLanes(withScreen, generateStep, PARALLEL_STEPS);
    setPhase(PHASES.DONE);
  };

  const close = () => {
    const changed = phase === PHASES.DONE;
    setPhase(PHASES.REVIEW);
    setRuns({});
    setFrozen(null);
    onClose({ changed });
  };

  const capturesLabel = (step) => {
    if (!step.screen) return LABELS.NO_CAPTURE;
    if (!step.states.length) return LABELS.SCREEN_ONLY;
    return t("CANVAS_EDITOR.FOLDER_IMPORT.SCREEN_AND_STATES", {
      count: step.states.length,
    });
  };

  const runLabel = (step) => {
    const run = runs[step.folder];
    if (!run) return null;
    const text =
      run.status === RUNS.DONE && run.states > 0
        ? t("CANVAS_EDITOR.FOLDER_IMPORT.DONE_STATES", { count: run.states })
        : get(LABELS.RUNS, run.status);
    return (
      <div className="canvas-folder-import__run">
        <Tag color={RUN_TONES[run.status]}>{text}</Tag>
        {run.reason ? <Text type="danger">{run.reason}</Text> : null}
        {(run.failures || []).map((failure) => (
          <Text key={failure.name} type="warning">
            {t("CANVAS_EDITOR.FOLDER_IMPORT.STATE_FAILED", failure)}
          </Text>
        ))}
      </div>
    );
  };

  const columns = [
    {
      title: LABELS.COLUMNS.STEP,
      dataIndex: "folder",
      width: 220,
      render: (folder, step) => (
        <div className="canvas-folder-import__step">
          <Text strong>{step.name}</Text>
          <Text type="secondary">{folder}</Text>
        </div>
      ),
    },
    {
      title: LABELS.COLUMNS.ACTION,
      dataIndex: "action",
      render: (action) => (
        <Tag color={ACTION_TONES[action]}>{get(LABELS.ACTIONS, action)}</Tag>
      ),
    },
    {
      title: LABELS.COLUMNS.CAPTURES,
      key: "captures",
      render: (_, step) => capturesLabel(step),
    },
    ...(phase === PHASES.REVIEW
      ? []
      : [
          {
            title: LABELS.COLUMNS.RESULT,
            key: "result",
            render: (_, step) => runLabel(step),
          },
        ]),
  ];

  const busy = [PHASES.SAVING, PHASES.GENERATING].includes(phase);
  const counts = Object.values(runs).reduce(
    (total, run) => ({
      done: total.done + (run.status === RUNS.DONE ? 1 : 0),
      failed: total.failed + (run.status === RUNS.FAILED ? 1 : 0),
    }),
    { done: 0, failed: 0 },
  );

  return (
    <Modal
      open={open}
      width={860}
      title={LABELS.TITLE}
      closable={!busy}
      maskClosable={false}
      onCancel={busy ? undefined : close}
      okText={
        phase === PHASES.REVIEW
          ? t("CANVAS_EDITOR.FOLDER_IMPORT.CONFIRM", {
              count: importable.length,
            })
          : LABELS.CLOSE
      }
      cancelText={LABELS.CANCEL}
      cancelButtonProps={{
        style: phase === PHASES.REVIEW ? undefined : { display: "none" },
      }}
      okButtonProps={{
        disabled: busy || (phase === PHASES.REVIEW && !importable.length),
        loading: busy,
      }}
      onOk={phase === PHASES.REVIEW ? onConfirm : close}
    >
      <div className="canvas-folder-import">
        {phase === PHASES.REVIEW ? <Text>{LABELS.INTRO}</Text> : null}
        {phase === PHASES.REVIEW ? (
          <Alert type="info" showIcon message={LABELS.SAVE_NOTE} />
        ) : null}
        {busy ? (
          <Alert
            type="warning"
            showIcon
            message={
              phase === PHASES.SAVING ? LABELS.SAVING : LABELS.RUNNING_NOTE
            }
          />
        ) : null}
        {phase === PHASES.DONE ? (
          <Alert
            type={
              counts.failed ||
              Object.values(runs).some((run) => (run.failures || []).length)
                ? "warning"
                : "success"
            }
            showIcon
            message={t("CANVAS_EDITOR.FOLDER_IMPORT.SUMMARY", counts)}
          />
        ) : null}
        {phase === PHASES.ERROR ? (
          <Alert type="error" showIcon message={LABELS.SAVE_FAILED} />
        ) : null}
        {plan.length ? (
          <Table
            size="small"
            pagination={false}
            rowKey="folder"
            columns={columns}
            dataSource={plan}
          />
        ) : (
          <Alert type="warning" showIcon message={LABELS.EMPTY} />
        )}
        {phase === PHASES.REVIEW && ignored.length ? (
          <Alert
            type="warning"
            showIcon
            message={t("CANVAS_EDITOR.FOLDER_IMPORT.IGNORED_TITLE", {
              count: ignored.length,
            })}
            description={
              <ul className="canvas-folder-import__ignored">
                {ignored.map(({ path, reason }) => (
                  <li key={path}>
                    <Text code>{path}</Text> {get(LABELS.IGNORED, reason)}
                  </li>
                ))}
              </ul>
            }
          />
        ) : null}
      </div>
    </Modal>
  );
};

FolderImportModal.propTypes = {
  files: PropTypes.arrayOf(PropTypes.object),
  nodes: PropTypes.arrayOf(PropTypes.object).isRequired,
  processId: PropTypes.string.isRequired,
  liveStepIds: PropTypes.arrayOf(PropTypes.string),
  onApplyPlan: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

FolderImportModal.defaultProps = {
  files: null,
  liveStepIds: [],
};

export default FolderImportModal;
